import { getEspnProjectionsCached, indexEspnPlayers, scoreProjection } from './espnProjections.js';

const SLEEPER_PLAYERS_ENDPOINT = 'https://api.sleeper.app/v1/players/nfl';
const NFL_REGULAR_WEEKS = 17;

const fetchJson = async (url, options = {}) => {
  const response = await fetch(url, options);
  if (!response.ok) {
    throw new Error(`Request failed (${response.status}) for ${url}`);
  }
  return response.json();
};

let sleeperPlayersCache = null;
const getSleeperPlayers = async () => {
  if (sleeperPlayersCache) {
    return sleeperPlayersCache;
  }
  sleeperPlayersCache = await fetchJson(SLEEPER_PLAYERS_ENDPOINT);
  return sleeperPlayersCache;
};

const sanitizePlayerId = (id) => (typeof id === 'string' ? id.trim() : id);

const formatNumber = (value) => {
  const fixed = Number(value || 0);
  if (Math.abs(fixed) >= 10) {
    return fixed.toFixed(1);
  }
  return fixed.toFixed(2);
};

const toPlayerRecord = ({
  id,
  name,
  position,
  team,
  support,
  against,
}) => {
  const supportValue = support || 0;
  const againstValue = against || 0;
  const net = supportValue - againstValue;
  return {
    id,
    name,
    position,
    team,
    support: supportValue,
    against: againstValue,
    net,
    displaySupport: formatNumber(supportValue),
    displayAgainst: formatNumber(againstValue),
    displayNet: formatNumber(net),
    importance: Math.abs(net),
    tags: {
      rootingFor: supportValue > 0,
      rootingAgainst: againstValue > 0,
    },
  };
};

const mapSleeperPlayers = (players) => {
  const mapped = new Map();
  Object.entries(players).forEach(([id, details]) => {
    const name =
      details?.full_name ||
      [details?.first_name, details?.last_name].filter(Boolean).join(' ') ||
      id;
    const position =
      details?.position || details?.fantasy_positions?.[0] || details?.depth_chart_position;
    const team = details?.team || details?.metadata?.team_abbr || 'FA';
    mapped.set(id, {
      id,
      name,
      position,
      team,
    });
  });
  return mapped;
};

const normalizeSleeperManagerTokens = (input) =>
  input
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);

const deriveSleeperRosterIds = (tokens, users, rosters) => {
  if (!tokens.length) {
    return new Set();
  }

  const normalizedTokens = tokens.map((token) => token.toLowerCase());
  const rosterIds = new Set();
  const matchedUserIds = new Set();

  users.forEach((user) => {
    const compareValues = [user?.display_name, user?.username, user?.user_id]
      .filter(Boolean)
      .map((value) => value.toString().toLowerCase());

    normalizedTokens.forEach((token, index) => {
      if (compareValues.includes(token)) {
        matchedUserIds.add(user.user_id);
        normalizedTokens[index] = null;
      }
    });
  });

  rosters.forEach((roster) => {
    const rosterId = roster?.roster_id;
    if (matchedUserIds.has(roster?.owner_id)) {
      rosterIds.add(rosterId);
      return;
    }

    const rosterTokenIndex = normalizedTokens.findIndex((token) => {
      if (!token) return false;
      if (/^\d+$/.test(token)) {
        return Number(token) === rosterId;
      }
      if (roster?.owner_id) {
        return roster.owner_id.toString().toLowerCase() === token;
      }
      return false;
    });

    if (rosterTokenIndex !== -1) {
      rosterIds.add(rosterId);
      normalizedTokens[rosterTokenIndex] = null;
    }
  });

  return rosterIds;
};

const matchupHasLiveScoring = (matchups) =>
  matchups.some((entry) => {
    const points = entry?.players_points || {};
    return Object.values(points).some((v) => typeof v === 'number' && v > 0);
  });

const buildEspnWeeklyLookup = async (playersMap) => {
  try {
    const payload = await getEspnProjectionsCached();
    const lookup = indexEspnPlayers(payload.players);
    return (playerId) => {
      const meta = playersMap.get(playerId);
      if (!meta) return null;
      const hit = lookup(meta);
      if (!hit) return null;
      const seasonTotal = scoreProjection(hit.stats) ?? hit.espnPprTotal;
      if (seasonTotal == null) return null;
      return Math.round((seasonTotal / NFL_REGULAR_WEEKS) * 10) / 10;
    };
  } catch {
    return () => null;
  }
};

const resolveSleeperStarterPoints = (playerId, entry, weeklyFor) => {
  const live = entry?.players_points?.[playerId];
  const sleeperProj = entry?.players_projected_points?.[playerId];

  if (typeof live === 'number' && live > 0) {
    return live;
  }
  if (typeof sleeperProj === 'number' && sleeperProj > 0) {
    return sleeperProj;
  }
  const espnWeekly = weeklyFor?.(playerId);
  if (espnWeekly != null) {
    return espnWeekly;
  }
  if (typeof live === 'number') {
    return live;
  }
  return 0;
};

const createSleeperExposures = (matchups, rosterIds, playersMap, weeklyFor) => {
  const groupedByMatchup = new Map();
  matchups.forEach((entry) => {
    const matchupId = entry?.matchup_id;
    if (!matchupId) return;
    if (!groupedByMatchup.has(matchupId)) {
      groupedByMatchup.set(matchupId, []);
    }
    groupedByMatchup.get(matchupId).push(entry);
  });

  const liveScoring = matchupHasLiveScoring(matchups);
  const exposures = new Map();

  groupedByMatchup.forEach((entries) => {
    const hasUserRoster = entries.some((entry) => rosterIds.has(entry.roster_id));
    if (!hasUserRoster) {
      return;
    }

    entries.forEach((entry) => {
      const starters = entry?.starters?.map(sanitizePlayerId).filter((id) => id && id !== '0') || [];
      const isUserRoster = rosterIds.has(entry.roster_id);

      starters.forEach((playerId) => {
        const pointValue = resolveSleeperStarterPoints(playerId, entry, weeklyFor);

        if (!exposures.has(playerId)) {
          const playerMeta = playersMap.get(playerId) || {
            id: playerId,
            name: playerId,
            position: 'FLEX',
            team: 'FA',
          };
          exposures.set(playerId, {
            id: playerId,
            name: playerMeta.name,
            position: playerMeta.position,
            team: playerMeta.team,
            support: 0,
            against: 0,
          });
        }

        const record = exposures.get(playerId);
        if (isUserRoster) {
          record.support += pointValue;
        } else {
          record.against += pointValue;
        }
      });
    });
  });

  return exposures;
};

const fetchSleeperRootingScores = async ({ leagueId, week, managerIdentifiers }) => {
  if (!leagueId) {
    throw new Error('Please provide a Sleeper league ID.');
  }
  if (!week) {
    throw new Error('Please choose a scoring week.');
  }

  const [leagueUsers, rosters, matchups, players] = await Promise.all([
    fetchJson(`https://api.sleeper.app/v1/league/${leagueId}/users`),
    fetchJson(`https://api.sleeper.app/v1/league/${leagueId}/rosters`),
    fetchJson(`https://api.sleeper.app/v1/league/${leagueId}/matchups/${week}`),
    getSleeperPlayers(),
  ]);

  const tokens = managerIdentifiers ? normalizeSleeperManagerTokens(managerIdentifiers) : [];
  const rosterIds = deriveSleeperRosterIds(tokens, leagueUsers, rosters);
  if (!rosterIds.size) {
    throw new Error(
      'We could not match any rosters for the managers you entered. Try using the Sleeper roster ID or username.'
    );
  }

  const playersMap = mapSleeperPlayers(players);
  const weeklyFor = await buildEspnWeeklyLookup(playersMap);
  const exposures = createSleeperExposures(matchups, rosterIds, playersMap, weeklyFor);

  const rosterIdToOwner = new Map();
  rosters.forEach((roster) => {
    rosterIdToOwner.set(roster?.roster_id, roster?.owner_id);
  });

  const managers = Array.from(rosterIds)
    .map((rosterId) => {
      const ownerId = rosterIdToOwner.get(rosterId);
      const owner = leagueUsers.find((user) => user?.user_id === ownerId);
      return owner?.display_name || owner?.username || owner?.user_id || `Roster ${rosterId}`;
    })
    .filter(Boolean);

  const playerResults = Array.from(exposures.values())
    .map((record) => toPlayerRecord(record))
    .sort((a, b) => b.importance - a.importance);

  const totals = playerResults.reduce(
    (acc, record) => {
      acc.support += record.support;
      acc.against += record.against;
      acc.net += record.net;
      return acc;
    },
    { support: 0, against: 0, net: 0 }
  );

  return {
    platform: 'Sleeper',
    week,
    managers,
    players: playerResults,
    totals,
  };
};

const POSITION_LOOKUP = {
  0: 'QB',
  1: 'TQB',
  2: 'RB',
  3: 'WR',
  4: 'TE',
  5: 'K',
  6: 'D/ST',
  16: 'DST',
};

const mapEspnPosition = (positionId) => POSITION_LOOKUP[positionId] || 'FLEX';

const parseEspnTeamIds = (input) =>
  input
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
    .map((value) => Number(value))
    .filter((value) => !Number.isNaN(value));

const buildEspnPlayerRecord = (entry, isUserTeam) => {
  const player = entry?.playerPoolEntry?.player || entry?.player;
  const playerId = player?.id || entry?.playerId;
  if (!playerId) {
    return null;
  }

  const appliedPoints =
    typeof entry?.totalPoints === 'number'
      ? entry.totalPoints
      : typeof entry?.appliedStatTotal === 'number'
      ? entry.appliedStatTotal
      : typeof entry?.points === 'number'
      ? entry.points
      : 0;

  return {
    id: playerId,
    name: player?.fullName || `${player?.firstName ?? ''} ${player?.lastName ?? ''}`.trim() || `Player ${playerId}`,
    position: mapEspnPosition(player?.defaultPositionId),
    team: player?.proTeamAbbreviation || player?.proTeamId || 'FA',
    support: isUserTeam ? appliedPoints : 0,
    against: isUserTeam ? 0 : appliedPoints,
  };
};

const createEspnExposures = (schedule, week, teamIds) => {
  const exposures = new Map();

  schedule
    .filter((matchup) => matchup?.matchupPeriodId === week)
    .forEach((matchup) => {
      const sides = [matchup?.home, matchup?.away].filter(Boolean);
      const involvesUser = sides.some((side) => teamIds.has(side?.teamId));
      if (!involvesUser) {
        return;
      }

      sides.forEach((side) => {
        const isUserTeam = teamIds.has(side?.teamId);
        const rosterEntries =
          side?.rosterForCurrentScoringPeriod?.entries ||
          side?.rosterForMatchupPeriod?.entries ||
          side?.awayTeamRoster?.entries ||
          side?.homeTeamRoster?.entries ||
          [];

        rosterEntries.forEach((entry) => {
          const playerRecord = buildEspnPlayerRecord(entry, isUserTeam);
          if (!playerRecord) return;
          if (!exposures.has(playerRecord.id)) {
            exposures.set(playerRecord.id, {
              id: playerRecord.id,
              name: playerRecord.name,
              position: playerRecord.position,
              team: playerRecord.team,
              support: 0,
              against: 0,
            });
          }
          const record = exposures.get(playerRecord.id);
          record.support += playerRecord.support;
          record.against += playerRecord.against;
        });
      });
    });

  return exposures;
};

const fetchEspnRootingScores = async ({ leagueId, season, week, teamIdsInput }) => {
  if (!leagueId) {
    throw new Error('Please provide an ESPN league ID.');
  }
  if (!season) {
    throw new Error('Provide the ESPN season year (e.g., 2024).');
  }
  if (!week) {
    throw new Error('Please choose a scoring week.');
  }

  const teamIds = new Set(parseEspnTeamIds(teamIdsInput || ''));
  if (!teamIds.size) {
    throw new Error('Enter at least one ESPN team ID that you manage.');
  }

  const response = await fetch(
    `https://fantasy.espn.com/apis/v3/games/ffl/seasons/${season}/segments/0/leagues/${leagueId}?view=mMatchupScore&view=mRoster`,
    {
      headers: {
        Accept: 'application/json',
        'X-Fantasy-Source': 'kona',
      },
    }
  );

  if (!response.ok) {
    throw new Error(
      response.status === 401 || response.status === 403
        ? 'ESPN rejected the request. Make sure the league is public or that you are routing the request through an authenticated proxy.'
        : `ESPN request failed (${response.status}).`
    );
  }

  const data = await response.json();
  const exposures = createEspnExposures(data?.schedule || [], week, teamIds);

  const managerNames = (data?.teams || [])
    .filter((team) => teamIds.has(team?.id))
    .map((team) => `${team?.location ?? ''} ${team?.nickname ?? ''}`.trim());

  const playerResults = Array.from(exposures.values())
    .map((record) => toPlayerRecord(record))
    .sort((a, b) => b.importance - a.importance);

  const totals = playerResults.reduce(
    (acc, record) => {
      acc.support += record.support;
      acc.against += record.against;
      acc.net += record.net;
      return acc;
    },
    { support: 0, against: 0, net: 0 }
  );

  return {
    platform: 'ESPN',
    week,
    managers: managerNames,
    players: playerResults,
    totals,
  };
};

// ─── Yahoo Fantasy API ────────────────────────────────────────────────────────

const YAHOO_API_BASE = 'https://fantasysports.yahooapis.com/fantasy/v2';

const fetchYahooJson = async (path, accessToken) => {
  const url = `${YAHOO_API_BASE}${path}?format=json`;
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      Accept: 'application/json',
    },
  });
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Yahoo session expired. Please reconnect your Yahoo account.');
    }
    throw new Error(`Yahoo API request failed (${response.status}) for ${path}`);
  }
  return response.json();
};

const parseYahooTeamIds = (input) =>
  (input || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean)
    .map(Number)
    .filter((v) => !Number.isNaN(v));

const mapYahooPosition = (pos) => {
  const positions = Array.isArray(pos) ? pos : [pos];
  const order = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF', 'D/ST', 'FLEX'];
  for (const p of order) {
    if (positions.includes(p)) return p;
  }
  return positions[0] || 'FLEX';
};

/**
 * Fetch rooting scores from the Yahoo Fantasy API.
 *
 * options:
 *   leagueKey   – e.g. "nfl.l.12345" (game_code.l.league_id)
 *   teamIds     – comma-separated team numbers within the league
 *   week        – NFL week number
 *   accessToken – valid Yahoo OAuth access token
 */
const fetchYahooRootingScores = async ({ leagueKey, teamIds: teamIdsInput, week, accessToken }) => {
  if (!leagueKey) {
    throw new Error('Please provide a Yahoo league key (e.g. nfl.l.12345).');
  }
  if (!accessToken) {
    throw new Error('Yahoo is not connected. Please complete the Yahoo sign-in flow.');
  }
  if (!week) {
    throw new Error('Please choose a scoring week.');
  }

  const managedTeamIds = new Set(parseYahooTeamIds(teamIdsInput || ''));
  if (!managedTeamIds.size) {
    throw new Error('Enter at least one Yahoo team number that you manage.');
  }

  // Fetch matchups for the given week with roster details
  const data = await fetchYahooJson(
    `/league/${leagueKey}/scoreboard;week=${week}`,
    accessToken
  );

  const matchups =
    data?.fantasy_content?.league?.[1]?.scoreboard?.['0']?.matchups || {};

  const exposures = new Map();

  Object.values(matchups).forEach((matchupEntry) => {
    if (typeof matchupEntry !== 'object' || !matchupEntry?.matchup) return;
    const matchup = matchupEntry.matchup;
    const teams = matchup['0']?.teams || {};

    const sides = Object.values(teams).filter(
      (entry) => typeof entry === 'object' && entry?.team
    );

    const involvesUser = sides.some((entry) => {
      const teamId = Number(entry.team[0]?.find((t) => t?.team_id)?.team_id);
      return managedTeamIds.has(teamId);
    });

    if (!involvesUser) return;

    sides.forEach((entry) => {
      const teamMeta = entry.team[0] || [];
      const teamId = Number(teamMeta.find?.((t) => t?.team_id)?.team_id);
      const isUserTeam = managedTeamIds.has(teamId);

      const rosterObj = entry.team[1]?.roster;
      const players = rosterObj?.['0']?.players || {};

      Object.values(players).forEach((playerEntry) => {
        if (typeof playerEntry !== 'object' || !playerEntry?.player) return;
        const playerData = playerEntry.player;
        const playerInfo = playerData[0] || [];
        const playerStats = playerData[1]?.player_points;

        const playerId = playerInfo.find?.((p) => p?.player_id)?.player_id;
        if (!playerId) return;

        const name = playerInfo.find?.((p) => p?.name)?.name?.full || `Player ${playerId}`;
        const positionData = playerInfo.find?.((p) => p?.primary_position)?.primary_position;
        const position = mapYahooPosition(positionData);
        const editorial_team = playerInfo.find?.((p) => p?.editorial_team_abbr)?.editorial_team_abbr || 'FA';

        const points = parseFloat(playerStats?.total ?? 0) || 0;

        const key = String(playerId);
        if (!exposures.has(key)) {
          exposures.set(key, {
            id: key,
            name,
            position,
            team: editorial_team.toUpperCase(),
            support: 0,
            against: 0,
          });
        }

        const record = exposures.get(key);
        if (isUserTeam) {
          record.support += points;
        } else {
          record.against += points;
        }
      });
    });
  });

  // Fetch manager names for the teams we manage
  let managerNames = [];
  try {
    const teamsData = await fetchYahooJson(`/league/${leagueKey}/teams`, accessToken);
    const teamsObj = teamsData?.fantasy_content?.league?.[1]?.teams || {};
    managerNames = Object.values(teamsObj)
      .filter((t) => typeof t === 'object' && t?.team)
      .filter((t) => {
        const teamId = Number(t.team[0]?.find?.((x) => x?.team_id)?.team_id);
        return managedTeamIds.has(teamId);
      })
      .map((t) => {
        const meta = t.team[0] || [];
        return meta.find?.((x) => x?.name)?.name || 'Unknown team';
      });
  } catch {
    // Non-critical – proceed without display names
  }

  const playerResults = Array.from(exposures.values())
    .map((record) => toPlayerRecord(record))
    .sort((a, b) => b.importance - a.importance);

  const totals = playerResults.reduce(
    (acc, record) => {
      acc.support += record.support;
      acc.against += record.against;
      acc.net += record.net;
      return acc;
    },
    { support: 0, against: 0, net: 0 }
  );

  return {
    platform: 'Yahoo',
    week,
    managers: managerNames,
    players: playerResults,
    totals,
  };
};

// ─── Dispatcher ───────────────────────────────────────────────────────────────

export const fetchRootingScores = async (options) => {
  const { platform } = options;
  if (platform === 'sleeper') {
    return fetchSleeperRootingScores(options);
  }
  if (platform === 'espn') {
    return fetchEspnRootingScores(options);
  }
  if (platform === 'yahoo') {
    return fetchYahooRootingScores(options);
  }
  throw new Error('Unsupported platform selected.');
};

export const helpers = {
  formatNumber,
};

